import { type LocationQueryValue, type NavigationGuardNext, type RouteLocationAsRelativeGeneric, type RouteLocationGeneric, type RouteLocationNormalized, type RouteLocationRaw } from "vue-router";
import type { LanguageCode } from "@/services/translation";
import { CountryCode, CountryCode2LanguageCodeMap, FallbackCountryCode, FallbackLanguageCode } from "@/services/translation";
import { getEnumKeyByValue, isStringInEnum } from "@/utilities";
import { RouteName } from "@/router/routeNameEnum";
import { createAiLookupFormLink, createLoginLink, createRegistrationLink } from "@/router/linkFactories";
import { type UserSettings } from "@/services/user/settings";
import { useUserSettings } from "@/composables/user";
import { checkUserOnInitialPageLoad, LoginMarkerService } from "@/services/user/auth/login";
import { useAppStore } from "@/composables";

export const isPublic = (route: RouteLocationNormalized): boolean => route.meta?.public === true;

export const createLocalizedRoute = (route: RouteLocationAsRelativeGeneric): RouteLocationRaw => {
  if (route.params === undefined || !route.params.countryCode) {
    // @todo Do not use FallbackCountryCode. It sohuld not be possible to create links when country code is not determined.
    // This is just a temp fallback.
    const countryCode = useAppStore().countryCode.value ?? FallbackCountryCode;
    route.params = route.params ?? {};
    route.params.countryCode = countryCode;
  }
  return route;
};

export const addHashToRoute = (route: RouteLocationRaw, hash: string): RouteLocationRaw => ({
  ...(route as object),
  hash,
});

export const goBackInHistory = () => window.history.back();

export const getLocalizationObject = (route: RouteLocationNormalized): { language: LanguageCode; country: CountryCode } | undefined => {
  const countryCodeString = route.params.countryCode as string;
  if (isStringInEnum(countryCodeString, CountryCode)) {
    const countryCode = getEnumKeyByValue(countryCodeString, CountryCode);
    const languageCode = CountryCode2LanguageCodeMap[countryCode];
    return { language: languageCode, country: countryCode };
  } else if (route.meta.allowNoCountryCode === true) {
    return { language: FallbackLanguageCode, country: FallbackCountryCode };
  } else {
    return undefined;
  }
};

export const redirectToAuth = (route: RouteLocationNormalized, next: NavigationGuardNext) => {
  let redirect;
  if (LoginMarkerService.wasSomeoneEverLoggedIn()) {
    const redirectQueryParam = route.fullPath;
    redirect = createLoginLink(redirectQueryParam);
  } else {
    redirect = createRegistrationLink();
  }
  return next(redirect);
};

export const getUserSettingsForRouter = async (): Promise<UserSettings | undefined> => {
  await checkUserOnInitialPageLoad(); // check if there is any token stored and possibly try to authenticate
  return useUserSettings().userSettings.value;
};

export const isRouteAccountSetup = (route: RouteLocationNormalized) => route.name === RouteName.AccountSetup;

/**
 * See: https://app.asana.com/1/1205261752347246/project/1205266520331763/task/1209763383274150
 */
export const handleMrSharkCampaignRedirect = (to: RouteLocationGeneric): RouteLocationRaw | undefined => {
  const categorySeoSlug = "lego-sets";
  const receivedData: Record<string, string | undefined | LocationQueryValue | LocationQueryValue[]> = {
    utm_source: to.query.utm_source,
    utm_medium: to.query.utm_medium,
    utm_campaign: to.query.utm_campaign,
    utm_term: to.query.utm_term,
    categorySeoSlug: to.params.seoSlug,
  };
  const expectedData: Record<string, string | undefined> = {
    utm_source: "Youtube",
    utm_medium: "Spoluprace",
    utm_campaign: "MrShark",
    utm_term: "duben 2025",
    categorySeoSlug,
  };
  const allMatch = Object.keys(receivedData).every((key) => receivedData[key] === expectedData[key]);
  if (allMatch) {
    return createAiLookupFormLink(categorySeoSlug);
  } else {
    return undefined;
  }
};
